import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Layout from '../components/layout'
import { SITE, POLICY_DATE } from '../site'
import './legal.css'

const GOOGLE_POLICY = 'https://developers.google.com/terms/api-services-user-data-policy'

const SECTIONS = [
  ['responsable', 'Responsable del tratamiento'],
  ['alcance', 'Alcance'],
  ['datos', 'Datos que recopilamos'],
  ['finalidades', 'Para qué usamos los datos'],
  ['google', 'Datos de Google en Secretario'],
  ['terceros', 'Con quién compartimos los datos'],
  ['transferencias', 'Transferencias internacionales'],
  ['conservacion', 'Conservación y eliminación'],
  ['seguridad', 'Seguridad'],
  ['derechos', 'Tus derechos y cómo ejercerlos'],
  ['menores', 'Menores de edad'],
  ['sitio', 'Sitio web y cookies'],
  ['cambios', 'Cambios a esta política'],
]

const PrivacyPolicy = () => (
  <Layout>
    <Helmet>
      <title>Política de privacidad | Variant</title>
      <meta
        name="description"
        content="Cómo Aplicaciones y Desarrollos Inteligentes S.A.S. (Variant) recopila, usa, protege y elimina los datos personales, incluidos los datos de Google que usa Secretario."
      />
      <link rel="canonical" href={`${SITE.url}/politica-de-privacidad`} />
    </Helmet>

    <article className="legal container">
      <header className="legal__head">
        <span className="eyebrow">Legal</span>
        <h1>Política de privacidad y tratamiento de datos personales</h1>
        <p className="legal__date">Vigente desde el {POLICY_DATE}</p>
      </header>

      <nav className="legal__toc" aria-label="Contenido">
        <strong>Contenido</strong>
        <ol>
          {SECTIONS.map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`}>{label}</a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="legal__body">
        <p>
          En {SITE.legalName} («Variant», «nosotros») protegemos la privacidad de las personas que usan nuestros
          productos y servicios. Esta política explica qué datos personales recopilamos, para qué los usamos, con quién
          los compartimos, cuánto tiempo los conservamos y cómo puedes ejercer tus derechos, de acuerdo con la Ley 1581
          de 2012, el Decreto 1377 de 2013 (compilado en el Decreto 1074 de 2015) y las demás normas colombianas de
          protección de datos personales.
        </p>

        <h2 id="responsable">1. Responsable del tratamiento</h2>
        <ul>
          <li>
            <strong>Razón social:</strong> {SITE.legalName}
          </li>
          {SITE.nit && (
            <li>
              <strong>NIT:</strong> {SITE.nit}
            </li>
          )}
          {SITE.city && (
            <li>
              <strong>Domicilio:</strong> {SITE.city}
            </li>
          )}
          <li>
            <strong>Correo electrónico:</strong> <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
          </li>
          <li>
            <strong>WhatsApp:</strong> {SITE.whatsappDisplay}
          </li>
          <li>
            <strong>Sitio web:</strong> <a href={SITE.url}>{SITE.url.replace('https://', '')}</a>
          </li>
        </ul>

        <h2 id="alcance">2. Alcance</h2>
        <p>Esta política aplica a los datos personales que tratamos a través de:</p>
        <ul>
          <li>este sitio web y nuestros canales de contacto (correo y WhatsApp);</li>
          <li>
            <strong>Secretario</strong>, nuestro asistente personal de inteligencia artificial en WhatsApp (
            <Link to="/secretario">variant.com.co/secretario</Link>);
          </li>
          <li>los servicios que prestamos a empresas, como agentes a la medida y automatización de procesos.</li>
        </ul>
        <p>
          Cuando prestamos un servicio a una empresa y tratamos datos de sus clientes por cuenta de ella, esa empresa es
          la responsable de esos datos y nosotros actuamos como encargados, según el contrato que firmemos con ella.
        </p>

        <h2 id="datos">3. Datos que recopilamos</h2>
        <h3>3.1 Cuando nos contactas</h3>
        <ul>
          <li>Nombre, número de teléfono o de WhatsApp y correo electrónico.</li>
          <li>El contenido de tus mensajes y consultas.</li>
        </ul>

        <h3>3.2 Cuando usas Secretario</h3>
        <ul>
          <li>
            <strong>Datos de registro:</strong> tu número de WhatsApp, tu nombre y tu zona horaria.
          </li>
          <li>
            <strong>Mensajes:</strong> los mensajes de texto y las notas de voz que le envías a Secretario, y sus
            respuestas. Las notas de voz se transcriben a texto para entenderlas.
          </li>
          <li>
            <strong>Memoria de la conversación:</strong> tus mensajes más recientes y las respuestas, para que
            Secretario entienda el contexto de lo que le pides.
          </li>
          <li>
            <strong>Avisos programados:</strong> los recordatorios y encargos que le pides programar, con su fecha, su
            hora y su repetición.
          </li>
          <li>
            <strong>Cuenta de Google conectada:</strong> el correo electrónico de la cuenta, los permisos que
            concediste y el token de autorización que nos permite actuar en tu nombre (ver la sección 5).
          </li>
          <li>
            <strong>Registros técnicos:</strong> datos de funcionamiento (fechas, resultados de cada operación, errores)
            que no incluyen el contenido de tus mensajes ni datos que te identifiquen.
          </li>
        </ul>

        <h2 id="finalidades">4. Para qué usamos los datos</h2>
        <ul>
          <li>
            <strong>Prestar el servicio:</strong> entender y cumplir lo que le pides a Secretario, enviarte sus
            respuestas y tus avisos programados por WhatsApp.
          </li>
          <li>
            <strong>Atenderte:</strong> responder tus consultas, gestionar tu acceso y darte soporte.
          </li>
          <li>
            <strong>Operar y proteger el servicio:</strong> detectar errores, prevenir abusos y mantener la seguridad.
          </li>
          <li>
            <strong>Comunicaciones:</strong> informarte sobre cambios importantes en el servicio o en esta política.
            Solo te enviaremos mensajes promocionales por WhatsApp si nos das tu consentimiento previo y explícito.
          </li>
        </ul>
        <p>
          <strong>No vendemos tus datos personales</strong>, no los usamos para publicidad y no usamos el contenido de
          tus conversaciones ni tus datos de Google para entrenar modelos de inteligencia artificial.
        </p>

        <h2 id="google">5. Datos de Google en Secretario</h2>
        <p>
          Si conectas tu cuenta de Google, Secretario accede a ella mediante OAuth, el sistema de autorización de
          Google. Solo te pide los permisos de las herramientas que vas a usar, y en cualquier momento puedes ver y
          quitar ese acceso.
        </p>
        <h3>5.1 Qué datos usa y para qué</h3>
        <div className="legal__table">
          <table>
            <thead>
              <tr>
                <th>Permiso</th>
                <th>Datos a los que accede</th>
                <th>Para qué los usa</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Gmail <code>gmail.send</code>
                </td>
                <td>Solo el envío de correos. No accede a los correos de tu bandeja.</td>
                <td>Enviar desde tu cuenta los correos que pides y confirmas.</td>
              </tr>
              <tr>
                <td>
                  Google Calendar <code>calendar.events</code>
                </td>
                <td>Los eventos de tu calendario: título, fecha, hora, invitados, descripción, lugar y enlace de Meet.</td>
                <td>Consultar tu agenda y crear, actualizar o cancelar los eventos que pides.</td>
              </tr>
              <tr>
                <td>
                  Google Tasks <code>tasks</code>
                </td>
                <td>Tus listas de tareas y sus tareas: título, notas, fecha límite y estado.</td>
                <td>Consultar tus tareas, crear tareas y marcarlas como completadas.</td>
              </tr>
              <tr>
                <td>
                  Google Contacts <code>contacts.readonly</code> <code>contacts.other.readonly</code>
                </td>
                <td>Nombre, correos y teléfonos de tus contactos y de tus «otros contactos». Solo lectura.</td>
                <td>Encontrar a la persona que mencionas para escribirle o invitarla.</td>
              </tr>
              <tr>
                <td>
                  Google Drive <code>drive.file</code>
                </td>
                <td>Solo los archivos que Secretario crea. No ve el resto de tu Drive.</td>
                <td>Crear documentos de Google Docs, listarlos y compartirlos con quien indicas.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>5.2 Cómo los tratamos</h3>
        <ul>
          <li>
            Secretario consulta tus datos de Google en el momento en que los necesita para responderte. No guarda una
            copia de tu agenda, tus tareas, tus contactos ni tus documentos; solo conserva lo que forme parte de la
            conversación reciente (ver la sección 8).
          </li>
          <li>
            Las acciones que cambian algo en tu cuenta (enviar un correo, crear, mover o cancelar un evento, crear o
            completar una tarea, crear o compartir un documento) solo se ejecutan después de que las confirmas.
          </li>
          <li>
            Para entender tu pedido y redactar la respuesta, el texto de la conversación y los datos de Google que
            hagan falta se procesan con el proveedor de inteligencia artificial indicado en la sección 6, únicamente
            para generar esa respuesta.
          </li>
          <li>
            El token de autorización de Google se guarda cifrado y solo lo usa el proceso que atiende tus pedidos.
          </li>
          <li>
            Nadie de nuestro equipo lee tus datos de Google, salvo que nos des tu consentimiento expreso para un caso
            concreto, que sea necesario por seguridad (por ejemplo, para investigar un abuso) o que lo exija la ley.
          </li>
        </ul>

        <h3>5.3 Uso Limitado</h3>
        <div className="legal__callout">
          <p>
            El uso y la transferencia a cualquier otra aplicación de la información recibida de las API de Google por
            parte de Secretario se ajustan a la{' '}
            <a href={GOOGLE_POLICY} target="_blank" rel="noopener noreferrer">
              Política de datos de usuario de los servicios de las API de Google
            </a>
            , incluidos los requisitos de Uso Limitado.
          </p>
          <p lang="en">
            <em>
              Secretario's use and transfer to any other app of information received from Google APIs will adhere to
              the{' '}
              <a href={GOOGLE_POLICY} target="_blank" rel="noopener noreferrer">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </em>
          </p>
        </div>
        <p>En particular:</p>
        <ul>
          <li>usamos los datos de Google solo para prestar y mejorar las funciones de Secretario que tú usas;</li>
          <li>
            no los transferimos a terceros, salvo a los proveedores necesarios para prestar el servicio, cuando lo
            exija la ley o como parte de una fusión o adquisición, con aviso previo;
          </li>
          <li>no los usamos para publicidad, ni personalizada ni de ningún otro tipo;</li>
          <li>no los vendemos ni los usamos para evaluar créditos o para préstamos;</li>
          <li>no los usamos para entrenar modelos de inteligencia artificial generales.</li>
        </ul>

        <h3>5.4 Cómo quitar el acceso</h3>
        <ul>
          <li>
            Escríbele a Secretario «desconecta mi cuenta de Google» y confirma: revocamos la autorización en Google y
            borramos el token de inmediato.
          </li>
          <li>
            También puedes quitar el acceso desde{' '}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
              myaccount.google.com/permissions
            </a>
            .
          </li>
        </ul>

        <h2 id="terceros">6. Con quién compartimos los datos</h2>
        <p>
          Compartimos datos solo con los proveedores que necesitamos para prestar el servicio, que los tratan por cuenta
          nuestra y bajo sus propias obligaciones de seguridad y confidencialidad:
        </p>
        <div className="legal__table">
          <table>
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Qué hace</th>
                <th>Datos</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Meta Platforms, Inc. (WhatsApp)</td>
                <td>Canal por el que te comunicas con nosotros y con Secretario.</td>
                <td>Número de WhatsApp, mensajes y notas de voz.</td>
              </tr>
              <tr>
                <td>OpenAI, L.L.C.</td>
                <td>
                  Modelos de inteligencia artificial que interpretan tus pedidos, redactan las respuestas y transcriben
                  las notas de voz.
                </td>
                <td>
                  Texto de la conversación reciente, notas de voz y los datos de Google necesarios para responder. Según
                  sus condiciones para la API, OpenAI no usa estos datos para entrenar sus modelos y puede conservarlos
                  hasta 30 días para vigilar abusos.
                </td>
              </tr>
              <tr>
                <td>Google LLC</td>
                <td>Las API de Gmail, Calendar, Tasks, Contacts y Drive que usas con Secretario.</td>
                <td>Las solicitudes que Secretario hace a tu cuenta para cumplir tus pedidos.</td>
              </tr>
              <tr>
                <td>Supabase, Inc.</td>
                <td>Base de datos.</td>
                <td>Datos de registro, avisos programados, cuentas conectadas y tokens cifrados.</td>
              </tr>
              <tr>
                <td>Oracle Corporation (Oracle Cloud Infrastructure)</td>
                <td>Servidores en los que funciona Secretario.</td>
                <td>Los datos que Secretario procesa mientras atiende tus pedidos.</td>
              </tr>
              <tr>
                <td>Amazon Web Services, Inc.</td>
                <td>Alojamiento de este sitio web.</td>
                <td>Datos técnicos de la visita, como la dirección IP.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          También podemos revelar datos cuando lo exija una autoridad competente o la ley, o para proteger los
          derechos, la seguridad o la propiedad de los usuarios o de Variant.
        </p>

        <h2 id="transferencias">7. Transferencias internacionales</h2>
        <p>
          Algunos de los proveedores anteriores tienen sus servidores fuera de Colombia, por ejemplo en Estados Unidos.
          Al usar nuestros servicios autorizas la transferencia o transmisión de tus datos a esos países, que se hace
          con proveedores que ofrecen niveles adecuados de protección, conforme a la normativa colombiana.
        </p>

        <h2 id="conservacion">8. Conservación y eliminación</h2>
        <ul>
          <li>
            <strong>Memoria de la conversación:</strong> los últimos 10 intercambios, que se borran automáticamente
            tras 24 horas sin conversación.
          </li>
          <li>
            <strong>Acciones por confirmar:</strong> se borran al confirmarse o a los 10 minutos si no se confirman.
          </li>
          <li>
            <strong>Avisos programados:</strong> hasta que se cumplen (si son de una sola vez), los cancelas o te das de
            baja.
          </li>
          <li>
            <strong>Cuenta de Google conectada y token:</strong> hasta que desconectas la cuenta o te das de baja.
          </li>
          <li>
            <strong>Datos de registro:</strong> mientras uses Secretario. Al darte de baja borramos tu registro junto con
            tus cuentas conectadas, tus avisos programados y la memoria de la conversación.
          </li>
          <li>
            <strong>Registros técnicos:</strong> por un tiempo limitado, para operar y proteger el servicio.
          </li>
          <li>
            <strong>Consultas comerciales y de soporte:</strong> mientras sean necesarias para atenderte y durante los
            plazos que exija la ley.
          </li>
        </ul>
        <p>
          Para darte de baja o pedir que borremos tus datos, escríbenos a{' '}
          <a href={`mailto:${SITE.email}`}>{SITE.email}</a> o por WhatsApp al {SITE.whatsappDisplay}.
        </p>

        <h2 id="seguridad">9. Seguridad</h2>
        <ul>
          <li>Todas las comunicaciones viajan cifradas (HTTPS/TLS).</li>
          <li>Los tokens de acceso a tus cuentas se guardan cifrados.</li>
          <li>
            La conexión con Google usa OAuth con PKCE y enlaces de autorización de un solo uso que vencen en pocos
            minutos.
          </li>
          <li>Solo atendemos mensajes de números registrados y verificamos la firma de cada notificación de WhatsApp.</li>
          <li>El acceso a los sistemas y a los datos está restringido a las personas y procesos que lo necesitan.</li>
        </ul>
        <p>
          Ningún sistema es completamente seguro. Si detectamos un incidente que afecte tus datos, te lo informaremos
          y avisaremos a las autoridades cuando la ley lo exija.
        </p>

        <h2 id="derechos">10. Tus derechos y cómo ejercerlos</h2>
        <p>Como titular de tus datos personales tienes derecho a:</p>
        <ul>
          <li>conocer, actualizar y rectificar tus datos;</li>
          <li>pedir prueba de la autorización que nos diste;</li>
          <li>ser informado sobre el uso que les hemos dado;</li>
          <li>revocar la autorización y pedir que borremos tus datos, cuando no exista un deber legal de conservarlos;</li>
          <li>acceder gratuitamente a tus datos;</li>
          <li>
            presentar quejas ante la Superintendencia de Industria y Comercio, después de haber agotado el trámite con
            nosotros.
          </li>
        </ul>
        <p>
          Para ejercerlos, escríbenos a <a href={`mailto:${SITE.email}`}>{SITE.email}</a> con tu nombre, tu número de
          WhatsApp o correo y tu solicitud. Respondemos las consultas en un plazo máximo de diez (10) días hábiles y los
          reclamos en un máximo de quince (15) días hábiles, prorrogables en los casos que prevé la ley.
        </p>

        <h2 id="menores">11. Menores de edad</h2>
        <p>
          Nuestros servicios están dirigidos a personas mayores de 18 años. No recopilamos de forma intencional datos de
          menores de edad; si detectamos que lo hicimos, los borraremos.
        </p>

        <h2 id="sitio">12. Sitio web y cookies</h2>
        <p>
          Este sitio no usa cookies de seguimiento ni herramientas de analítica o publicidad. Para mostrar las fuentes
          tipográficas, tu navegador las descarga de Google Fonts, que puede registrar datos técnicos como tu dirección
          IP. Los enlaces a WhatsApp y a otros sitios se rigen por las políticas de esos terceros.
        </p>

        <h2 id="cambios">13. Cambios a esta política</h2>
        <p>
          Podemos actualizar esta política. Publicaremos la nueva versión en esta página con su fecha de vigencia y, si
          los cambios son importantes, te avisaremos por WhatsApp o por correo antes de que apliquen. Si el cambio
          implica usar tus datos de Google de una forma distinta a la descrita aquí, te pediremos tu consentimiento.
        </p>

        <p className="legal__related">
          Consulta también los <Link to="/terminos">términos y condiciones</Link>.
        </p>
      </div>
    </article>
  </Layout>
)

export default PrivacyPolicy
